<template>
  <div
    v-if="screenSize === 'mobile' && rates?.length > 0"
    class="grid grid-cols-2 gap-3 border-t border-placeholder-gray p-4"
  >
    <div class="col-span-2 flex justify-between">
      <p>
        {{ totalRates }}
      </p>
    </div>
    <div
      v-for="(rate, index) in rates"
      :key="`rate-${index}`"
      class="col-span-2 rounded-lg border border-placeholder-gray bg-white shadow-md sm:col-span-1"
    >
      <router-link
        :to="{
          name: 'Rate Confirmation',
          params: { id: rate.id },
        }"
      >
        <RateConfirmationCard
          :rate="rate"
          @accept-terms="emitAcceptTerms"
        ></RateConfirmationCard>
      </router-link>
    </div>
  </div>
  <div
    v-else-if="screenSize === 'mobile' && rates?.length === 0"
    class="grid grid-cols-2 gap-3 border-t border-placeholder-gray p-4"
  >
    <div
      class="col-span-2 mt-8 flex flex-col items-center justify-center rounded-lg border border-placeholder-gray bg-white py-8 shadow-md"
    >
      <img
        class="h-12 w-12"
        src="@/assets/magnify.svg"
        alt="magnify icon"
      />

      <h3 class="text-gray-over-dark">No rate confirmation results found.</h3>
    </div>
  </div>

  <div
    v-else
    class="w-full"
  >
    <div
      class="grid w-full grid-cols-12 gap-2 px-1 pb-1 text-left text-sm font-semibold text-dark-blue"
    >
      <div class="col-span-4 pl-3 pr-1 text-xs xl:col-span-5">
        {{ isBrokerUser ? 'Carrier' : 'Subject' }}
      </div>
      <div class="col-span-2 pr-1 text-xs">
        {{ isBrokerUser ? 'Identifier' : 'Broker' }}
      </div>
      <div class="col-span-2 px-1 text-xs">Created by</div>
      <div class="col-span-2 px-1 text-xs">Created at</div>
      <div class="col-span-1 px-1 text-xs">Status</div>
    </div>
    <div
      v-if="!rates || rates?.length == 0"
      class="col-span-20 flex min-h-[300px] flex-col items-center justify-center rounded-lg bg-gray-100"
    >
      <img
        class="h-12 w-12"
        src="@/assets/magnify.svg"
        alt="magnify icon"
      />

      <h3 class="pb-10 text-gray-over-dark">
        No rate confirmation results found.
      </h3>
    </div>

    <div
      v-for="(rate, index) in rates"
      :key="`rate-${index}`"
      class="grid w-full grid-cols-12 gap-2 border-b-2 border-l-2 border-r-2 border-light-gray py-3 pl-1 pr-3 text-sm font-normal text-neutral-subtle hover:bg-light-gray"
      :class="{
        'rounded-t-lg border-t-2': index == 0,
        'rounded-b-lg border-b-2': index == rates?.length - 1,
        'bg-off-white': index % 2 == 1,
      }"
    >
      <router-link
        :to="{
          name: 'Rate Confirmation',
          params: { id: rate.id },
        }"
        class="col-span-4 flex items-center gap-2 pl-3 pr-4 text-left xl:col-span-5"
      >
        <img
          v-if="isBrokerUser"
          class=""
          src="@/assets/highway-badge-1.svg"
          alt="highway badge"
        />
        <p v-if="isBrokerUser">
          {{ capitalize(rate.carrier?.legal_name || '') || '-' }}
        </p>
        <p v-else>
          {{ rate.subject || '-' }}
        </p>
      </router-link>

      <router-link
        :to="{
          name: 'Rate Confirmation',
          params: { id: rate.id },
        }"
        class="col-span-2 flex items-center"
      >
        <div
          v-if="isBrokerUser"
          class="flex flex-col"
        >
          <p
            v-if="dotIdentifier(rate.carrier.identifiers)"
            class="inline-block text-left"
          >
            DOT: {{ dotIdentifier(rate.carrier.identifiers).value_non_padded }}
          </p>
          <p
            v-if="mcIdentifier(rate.carrier.identifiers)"
            class="inline-block text-left"
          >
            MC: {{ mcIdentifier(rate.carrier.identifiers).value_non_padded }}
          </p>
        </div>

        <div
          v-else
          class="flex flex-col"
        >
          <span class="inline-block text-left">
            {{ rate?.issuer?.legal_name }}
          </span>

          <span
            v-if="rate?.issuer?.dba_name"
            class="text-left text-xs uppercase text-body-color"
          >
            {{ rate?.issuer?.dba_name }}
          </span>
        </div>
      </router-link>

      <router-link
        :to="{
          name: 'Rate Confirmation',
          params: { id: rate.id },
        }"
        class="col-span-2 flex items-center px-1"
      >
        <span
          v-if="rate?.creator?.full_name"
          class="text-left"
        >
          {{ rate?.creator?.full_name }}
        </span>
        <span v-else>-</span>
      </router-link>

      <router-link
        :to="{
          name: 'Rate Confirmation',
          params: { id: rate.id },
        }"
        class="col-span-2 flex items-center justify-start px-1"
      >
        <span
          v-if="rate?.created_at"
          class="text-left"
        >
          {{ dayjs(rate?.created_at).format('h:mm a on M/D/YYYY') }}
        </span>
        <span v-else>-</span>
      </router-link>

      <router-link
        :to="{
          name: 'Rate Confirmation',
          params: { id: rate.id },
        }"
        class="col-span-1 flex items-center px-1"
      >
        <span
          v-if="rate?.status"
          class="rounded-md bg-blue-100 px-3 py-1 font-medium capitalize text-blue-800"
          :class="{
            'bg-green-100 !text-green-800':
              rate?.terms_accepted_at &&
              !(rate?.cancelled_at || rate?.rejected_at),
            'bg-red-100 text-danger-default':
              rate?.cancelled_at || rate?.rejected_at,
          }"
        >
          {{ rateStatus(rate) }}
        </span>
        <span v-else>-</span>
      </router-link>

      <!-- <div class="col-span-3 flex h-14 items-center gap-2 px-1 pr-3"> -->
      <!-- <div v-if="!carrierHasAccepted(rate)">
          <button-main
            size="sm"
            style="font-size: 0.65rem; padding: 4px 16px"
            :is-loading="isLoading"
            @click="emitAcceptTerms(rate)"
          >
            Review & Accept Terms
          </button-main>
        </div>
        <div v-else>
          <div
            class="flex items-center gap-1 text-yellow-500"
            style="font-size: 0.75rem"
          >
            <svg
              class="h-4 w-4"
              fill="currentColor"
              viewBox="0 0 13 13"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.30466 12.2119C9.35139 12.2119 11.8212 9.74201 11.8212 6.69529C11.8212 5.8144 11.6148 4.98173 11.2476 4.24303L6.49714 8.99348L5.93988 9.55074L5.38262 8.99348L2.95534 6.56621L4.06986 5.45169L5.93988 7.32171L10.3341 2.92748C9.32739 1.8513 7.89458 1.17871 6.30466 1.17871C3.25794 1.17871 0.788086 3.64857 0.788086 6.69529C0.788086 9.74201 3.25794 12.2119 6.30466 12.2119Z"
              />
            </svg>
            <span>Accepted</span>
          </div>
        </div> -->
      <!-- <div v-if="!carrierHasAccepted(rate)" class="w-56 text-right">
            <button-main @click="openTermsModal" variant="primary">
              Review & Accept Terms
            </button-main>
          </div>
          <div v-else class="w-56 text-right">
            <button-main :disabled="true" variant="outline">
              Terms Accepted
            </button-main>
          </div> -->
      <!-- <div v-if="rate?.attachments?.length > 0" class="relative w-full">
            <div class="flex justify-between items-start gap-0 w-full">
              <div v-for="(file, idx) in rate?.attachments" :key="`rate-attachment-${file?.id}-${idx}`" class="flex flex-col gap-2">
                  <div @click.stop @click.prevent="downloadAttachment(rate?.id, idx)" class="inline-flex items-center text-left text-yellow-500 group cursor-pointer">
                      <svg class="pr-1.5 relative inline-block flex-shrink-0 h-6 w-6 text-yellow-500 group-hover:text-yellow-600" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 14.3906L11.2929 15.0977L12 15.8048L12.7071 15.0977L12 14.3906ZM7.29289 11.0977L11.2929 15.0977L12.7071 13.6835L8.70711 9.68352L7.29289 11.0977ZM12.7071 15.0977L16.7071 11.0977L15.2929 9.68352L11.2929 13.6835L12.7071 15.0977Z" fill="currentColor"/>
                          <path d="M12 14.3906L12 4.39063" stroke="currentColor" stroke-width="2"/>
                          <path d="M5 9.39062V18.3906H19V9.39062" stroke="currentColor" stroke-width="2"/>
                      </svg>
                      <span class="text-xs group-hover:text-yellow-600 text-left" style="line-height: 1.3;">{{ file?.filename ? file?.filename : 'View File' }}</span>
                  </div>
              </div>
            </div>
          </div> -->
      <!-- </div> -->
    </div>
  </div>
</template>

<script>
import { watch, toRef, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import RateConfirmationCard from './Cards/RateConfirmationCard.vue';
import { capitalize } from '@/common/validators';

export default {
  name: 'RateConfirmationTable',
  components: {
    RateConfirmationCard,
  },
  props: {
    rateData: {
      type: Array,
      required: true,
    },
    onRemove: {
      type: Function,
    },
    searchText: {
      type: String,
      required: false,
      default: '',
    },
    screenSize: String,
  },
  emits: ['sort', 'accept-terms'],
  setup(props, { emit }) {
    const router = useRouter();
    const store = useStore();
    const searchTextVal = toRef(props, 'searchText');
    const ratesArray = toRef(props, 'rateData');
    const rates = ref([...ratesArray.value]);
    const isLoading = ref(false);

    const isBrokerUser = computed(
      () => store.state.users?.currentUser?.company?.company_type === 'broker'
    );

    const dotIdentifier = (identifiers) => {
      return identifiers?.find((el) => el.is_type === 'DOT');
    };

    const mcIdentifier = (identifiers) => {
      return identifiers?.find((el) => el.is_type === 'MC');
    };

    const totalRates = computed(() => {
      let total = '';
      if (rates.value?.length === 1) {
        total = '1 Rate Con';
      } else if (rates.value?.length > 1) {
        total = `${rates.value?.length} Rate Cons`;
      }
      return total;
    });

    const sort = (s) => {
      emit('sort', s);
    };

    const onRowClick = async (rate) => {
      // store.dispatch('carriers/getRateDetail', rate?.id);

      router.push({
        name: 'Rate Confirmation',
        params: { id: rate?.id },
      });
    };

    const resolveUrl = (broker) => {
      const route = router.resolve({
        name: 'Carrier Brokers Show Page',
        params: { id: broker?.id },
      });

      return route.href;
    };

    const rateStatus = (rate) => {
      if (rate.status === 'rejected') {
        return 'Rejected';
      } else if (rate?.terms_accepted_at) {
        return 'Accepted';
      } else if (rate.status === 'delivered') {
        return 'new';
      } else {
        return rate.status;
      }
    };

    // const carrierHasAccepted = (rate) => {
    //   return rate?.recent_rate_confirmation_activities?.some(r => r?.is_type == 'terms_accepted');
    // }

    const downloadAttachment = async (id, index) => {
      const response = await store.dispatch('rates/downloadRateAttachment', {
        id: id,
        attachment_index: index,
      });
      if (response?.data) {
        const blob = new Blob([response.data]);
        const contentDisposition = response.headers['content-disposition'];
        const filename = contentDisposition.match(/filename\*=UTF-8''(.+)/)[1];
        const decodedFilename = decodeURIComponent(filename);
        // IE doesn't support download attribute on anchor tags, so check if browser is IE or not
        if (window?.navigator && window?.navigator?.msSaveOrOpenBlob) {
          // If IE, use proprietary msSaveOrOpenBlob method
          window?.navigator?.msSaveOrOpenBlob(blob, decodedFilename);
        } else {
          // If not IE, create a temporary link element, simulate click, then remove
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', decodedFilename);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      }
    };

    watch(ratesArray, (newRates) => {
      rates.value = [...newRates];
    });

    const carrierHasAccepted = (rate) => {
      return rate?.terms_accepted_at ? true : false;
      // if (rate?.recent_rate_confirmation_activities?.length > 0) {
      //   return rate?.recent_rate_confirmation_activities?.some(r => r?.is_type == 'terms_accepted');
      // } else {
      //   return rate?.rate_confirmation_activities?.some(r => r?.is_type == 'terms_accepted') || false;
      // }
    };

    const getBrokerIdentifiers = (issuer) => {
      const mainTypes = ['MC'];
      return issuer.identifiers?.filter((identifier) =>
        mainTypes?.includes(identifier?.is_type)
      );
    };

    const emitAcceptTerms = (rate) => {
      emit('accept-terms', rate);
    };

    return {
      sort,
      onRowClick,
      resolveUrl,
      dayjs,
      rates,
      totalRates,
      searchTextVal,
      downloadAttachment,
      carrierHasAccepted,
      getBrokerIdentifiers,
      emitAcceptTerms,
      isLoading,
      rateStatus,
      isBrokerUser,
      capitalize,
      dotIdentifier,
      mcIdentifier,
    };
  },
};
</script>

<style scoped>
.action-column {
  width: 48px;
}
</style>
